import React, { useEffect, useState } from "react";
import {
  BrowserRouter,
  Link,
  NavLink,
  Redirect,
  Route,
  Switch,
  useLocation,
  useParams,
} from "react-router-dom";
import { Helmet } from "react-helmet";
import {
  profile,
  capabilities,
  learning,
  education,
  experience,
  awards,
  languages,
} from "./profile";
import { projects } from "./projects";
import ProjectVisual from "./ProjectVisual";
import "./Portfolio.css";

function Meta({ title, description = profile.summary }) {
  const { pathname } = useLocation();
  const pageTitle = title
    ? `${title} | ${profile.name}`
    : `${profile.name} | MLOps & Robot Learning`;
  const url = profile.url + pathname;
  return (
    <Helmet>
      <title>{pageTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={`${profile.url}/icons/desc.png`} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={`${profile.url}/icons/desc.png`} />
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Person",
          name: profile.name,
          url: profile.url,
          jobTitle: "Research Assistant",
          worksFor: {
            "@type": "Organization",
            name: "Carnegie Mellon University Africa",
          },
          description: profile.summary,
          email: profile.email,
          sameAs: [profile.github, profile.linkedin],
          image: `${profile.url}/images/patrick-cutout.png`,
          address: {
            "@type": "PostalAddress",
            addressLocality: "Kigali",
            addressCountry: "RW",
          },
        })}
      </script>
    </Helmet>
  );
}
function Navigation({ theme, toggleTheme }) {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  useEffect(() => {
    setOpen(false);
  }, [pathname]);
  return (
    <header className="site-header">
      <div className="header-inner">
        <Link className="brand" to="/" aria-label="Patrick Niyigena home">
          <span className="brand-mark">
            PN<span>·</span>
          </span>
          <span className="brand-name">Patrick Niyigena</span>
        </Link>
        <div className="nav-controls">
          <button
            className="theme-button"
            onClick={toggleTheme}
            aria-label={`Switch to ${theme === "dark" ? "light" : "dark"} mode`}
          >
            {theme === "dark" ? "Light ◐" : "Dark ◑"}
          </button>
          <button
            className="menu-button"
            aria-expanded={open}
            aria-controls="main-navigation"
            onClick={() => setOpen(!open)}
          >
            Menu {open ? "−" : "+"}
          </button>
        </div>
        <nav
          id="main-navigation"
          className={open ? "navigation is-open" : "navigation"}
          aria-label="Main navigation"
        >
          <NavLink to="/projects">Projects</NavLink>
          <NavLink to="/about">About & experience</NavLink>
          <NavLink to="/resume">CV</NavLink>
          <NavLink className="contact-nav" to="/contact">
            Let’s talk <span aria-hidden="true">↗</span>
          </NavLink>
        </nav>
      </div>
    </header>
  );
}
function Tags({ values }) {
  return (
    <div className="tag-list">
      {values.map((v) => (
        <span key={v}>{v}</span>
      ))}
    </div>
  );
}
function ProjectCard({ project, index }) {
  return (
    <article className="project-card">
      <Link
        className="project-image-link"
        to={`/projects/${project.id}`}
        aria-label={`Read ${project.title} case study`}
      >
        <ProjectVisual kind={project.visual} compact />
      </Link>
      <div className="project-card-content">
        <div className="eyebrow">
          {String(index + 1).padStart(2, "0")} / {project.category}
        </div>
        <h3>
          <Link to={`/projects/${project.id}`}>
            {project.title}
            <span aria-hidden="true">↗</span>
          </Link>
        </h3>
        <p>{project.summary}</p>
        <Tags values={project.tags} />
        <Link className="text-link" to={`/projects/${project.id}`}>
          Read case study <span aria-hidden="true">→</span>
        </Link>
      </div>
    </article>
  );
}
function ActionLabel({ children, detail, icon = "arrow" }) {
  return (
    <>
      <span className="action-copy">
        {detail && <span className="action-detail">{detail}</span>}
        <span className="action-title">{children}</span>
      </span>
      <span className={`action-icon icon-${icon}`} aria-hidden="true">
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
          focusable="false"
        >
          {icon === "document" ? (
            <>
              <path d="M6 3h8l4 4v14H6z" />
              <path d="M14 3v5h4M9 12h6M9 16h6" />
            </>
          ) : icon === "download" ? (
            <>
              <path d="M12 3v12m-5-5 5 5 5-5M5 17v4h14v-4" />
            </>
          ) : (
            <path d="M5 19 19 5M5 5h14v14" />
          )}
        </svg>
      </span>
    </>
  );
}
function ContactStrip() {
  return (
    <section className="contact-strip">
      <div>
        <p className="eyebrow">LET’S BUILD SOMETHING USEFUL</p>
        <h2>Have a problem in mind?</h2>
        <p>Seeking MLOps opportunities · Open to remote roles.</p>
      </div>
      <Link className="button primary" to="/contact">
        <ActionLabel>Get in touch</ActionLabel>
      </Link>
    </section>
  );
}
function Home() {
  return (
    <>
      <Meta />
      <section className="hero">
        <div className="hero-copy">
          <p className="eyebrow">
            <span className="signal-dot" /> OPEN TO MLOPS OPPORTUNITIES ·
            KIGALI, RWANDA
          </p>
          <h1>
            Reliable ML systems.
            <br />
            <em>Intelligent machines.</em>
          </h1>
          <p className="hero-summary">{profile.summary}</p>
          <p className="hero-interests">
            <span>RESEARCH INTERESTS</span>
            {profile.researchInterests}
          </p>
          <div className="button-row">
            <Link className="button primary" to="/projects">
              <ActionLabel detail="SELECTED PROJECTS">
                Explore my work
              </ActionLabel>
            </Link>
            <Link className="button secondary" to="/resume">
              <ActionLabel detail="THE EXPERIENCE BEHIND IT" icon="document">
                Read my CV
              </ActionLabel>
            </Link>
          </div>
          <div className="hero-context">
            <span>Patrick Niyigena</span>
            <span>MSc candidate · CMU Africa</span>
          </div>
        </div>
        <aside
          className="hero-portrait-scene"
          aria-label="Patrick Niyigena and current research"
        >
          <div
            className="portrait-backdrop"
            role="img"
            aria-label="Portrait of Patrick Niyigena"
          />
          <Link to="/projects/robot-learning" className="portrait-research">
            <div>
              <span className="eyebrow">CURRENT RESEARCH</span>
              <p>Adversarial robot learning</p>
              <span>Physical interaction & co-adaptation</span>
            </div>
            <span className="research-arrow" aria-hidden="true">
              ↗
            </span>
          </Link>
        </aside>
      </section>
      <section className="section-block">
        <div className="section-heading">
          <div>
            <p className="eyebrow">01 / SELECTED WORK</p>
            <h2>Ideas, built into systems.</h2>
          </div>
          <Link className="text-link" to="/projects">
            All projects ↗
          </Link>
        </div>
        <div className="project-grid">
          {projects
            .filter((p) => p.featured)
            .map((p, i) => (
              <ProjectCard key={p.id} project={p} index={i} />
            ))}
        </div>
      </section>
      <section className="story-section section-block">
        <div>
          <p className="eyebrow">02 / THE THREAD THROUGH MY WORK</p>
          <h2>
            From physical systems
            <br />
            to intelligent software.
          </h2>
        </div>
        <div>
          <p>{profile.about}</p>
          <Link className="text-link" to="/about">
            More about my background ↗
          </Link>
        </div>
      </section>
      <section className="section-block">
        <div className="section-heading">
          <div>
            <p className="eyebrow">03 / TECHNICAL FOCUS</p>
            <h2>A connected engineering toolkit.</h2>
          </div>
        </div>
        <div className="capability-grid">
          {capabilities.map((c, i) => (
            <article className="capability" key={c.title}>
              <span className="capability-number">0{i + 1}</span>
              <h3>{c.title}</h3>
              <p>{c.description}</p>
              <Tags values={c.skills} />
            </article>
          ))}
        </div>
        <p className="learning">
          <strong>Currently learning</strong> {learning.join(" · ")}
        </p>
      </section>
      <ContactStrip />
    </>
  );
}
function Projects() {
  return (
    <>
      <Meta title="Projects" />
      <div className="page-intro">
        <p className="eyebrow">SELECTED WORK</p>
        <h1>
          Systems I’ve helped
          <br />
          <em>bring to life.</em>
        </h1>
        <p>
          AI applications, geospatial data, embedded monitoring, and robot
          learning. Explore the problem, my contribution, and the outcome of
          each project.
        </p>
      </div>
      <div className="project-grid">
        {projects.map((p, i) => (
          <ProjectCard key={p.id} project={p} index={i} />
        ))}
      </div>
      <p className="project-note">
        Project code and demos are currently private. These case studies
        describe my work; diagrams are conceptual illustrations.
      </p>
      <ContactStrip />
    </>
  );
}
function ProjectDetail() {
  const { slug } = useParams();
  const project = projects.find((p) => p.id === slug);
  if (!project) return <NotFound />;
  return (
    <>
      <Meta title={project.title} description={project.summary} />
      <div className="page-intro case-intro">
        <Link className="text-link" to="/projects">
          ← All projects
        </Link>
        <p className="eyebrow">
          {project.category}
          {project.status ? ` · ${project.status}` : ""}
        </p>
        <h1>{project.title}</h1>
        <p>{project.subtitle}</p>
        <Tags values={project.tags} />
      </div>
      <figure className="case-visual">
        <ProjectVisual kind={project.visual} />
        <figcaption>Conceptual workflow illustration</figcaption>
      </figure>
      <div className="case-layout">
        <aside>
          <p className="eyebrow">PROJECT ACCESS</p>
          <p>Code and demos are private.</p>
          <a
            className="button secondary"
            href={`mailto:${profile.email}?subject=${encodeURIComponent(
              `Let's discuss ${project.title}`
            )}`}
          >
            <ActionLabel>Discuss this project</ActionLabel>
          </a>
        </aside>
        <div>
          <section className="case-section">
            <h2>The problem</h2>
            <p>{project.challenge}</p>
          </section>
          <section className="case-section">
            <h2>My contribution</h2>
            <ul>
              {project.contribution.map((p) => (
                <li key={p}>{p}</li>
              ))}
            </ul>
          </section>
          <section className="case-section">
            <h2>The workflow</h2>
            <ol className="flow-steps">
              {project.flow.map((s) => (
                <li key={s}>{s}</li>
              ))}
            </ol>
          </section>
          <section className="case-section">
            <h2>{project.status ? "Current direction" : "Outcome"}</h2>
            <p>{project.outcome}</p>
            {project.metrics.length > 0 && (
              <div className="metrics">
                {project.metrics.map((m) => (
                  <div key={m.label}>
                    <strong>{m.value}</strong>
                    <span>{m.label}</span>
                  </div>
                ))}
              </div>
            )}
            <p className="evidence-note">{project.note}</p>
          </section>
        </div>
      </div>
      <ContactStrip />
    </>
  );
}
function Education({ standalone = false }) {
  return (
    <>
      {standalone && <Meta title="Education" />}
      <section className="section-block" id="education">
        <div className="section-heading">
          <div>
            <p className="eyebrow">ACADEMIC FOUNDATION</p>
            {standalone ? <h1>Education</h1> : <h2>Education</h2>}
          </div>
        </div>
        <div className="education-list">
          {education.map((e) => (
            <article key={e.school}>
              <div className="timeline-date">{e.dates}</div>
              <div>
                <h3>{e.school}</h3>
                <p className="degree">{e.degree}</p>
                <p>{e.detail}</p>
                <span className="gpa">GPA {e.gpa}</span>
              </div>
            </article>
          ))}
        </div>
      </section>
    </>
  );
}
function About() {
  return (
    <>
      <Meta title="About & experience" />
      <div className="page-intro">
        <p className="eyebrow">ABOUT PATRICK</p>
        <h1>
          An engineering foundation.
          <br />
          <em>A focus on ML systems.</em>
        </h1>
        <p>{profile.about}</p>
        <p>{profile.interests}</p>
      </div>
      <section className="section-block" id="experience">
        <div className="section-heading">
          <div>
            <p className="eyebrow">RESEARCH & ENGINEERING</p>
            <h2>Experience</h2>
          </div>
          <a className="text-link" href={profile.cv} download>
            Download CV ↓
          </a>
        </div>
        <div className="timeline">
          {experience.map((e) => (
            <article key={e.company}>
              <div className="timeline-date">
                {e.dates}
                <span>{e.location}</span>
              </div>
              <div>
                <h3>{e.role}</h3>
                <p className="company">{e.company}</p>
                <ul>
                  {e.points.map((p) => (
                    <li key={p}>{p}</li>
                  ))}
                </ul>
              </div>
            </article>
          ))}
        </div>
      </section>
      <Education />
      <section className="section-block recognition">
        <div>
          <p className="eyebrow">RECOGNITION</p>
          <h2>Awards</h2>
          <ul>
            {awards.map((a) => (
              <li key={a}>{a}</li>
            ))}
          </ul>
        </div>
        <div>
          <p className="eyebrow">ACROSS CULTURES</p>
          <h2>Languages</h2>
          <ul>
            {languages.map((l) => (
              <li key={l}>{l}</li>
            ))}
          </ul>
        </div>
      </section>
      <ContactStrip />
    </>
  );
}
function Resume() {
  return (
    <>
      <Meta title="CV" />
      <div className="page-intro">
        <p className="eyebrow">EXPERIENCE AT A GLANCE</p>
        <h1>My curriculum vitae.</h1>
        <p>
          Software engineering, backend development, and AI / machine learning.
        </p>
        <div className="button-row">
          <a className="button primary" href={profile.cv} download>
            <ActionLabel icon="download">Download CV</ActionLabel>
          </a>
          <a
            className="button secondary"
            href={profile.cv}
            target="_blank"
            rel="noopener noreferrer"
          >
            <ActionLabel icon="document">Open PDF</ActionLabel>
          </a>
        </div>
      </div>
      <object
        className="cv-viewer"
        data={profile.cv}
        type="application/pdf"
        aria-label="Patrick Niyigena curriculum vitae"
      >
        <p>
          Your browser cannot display this PDF inline.{" "}
          <a href={profile.cv}>Open or download the CV</a>.
        </p>
      </object>
    </>
  );
}
function Contact() {
  return (
    <>
      <Meta title="Contact" />
      <div className="page-intro">
        <p className="eyebrow">LET’S CONNECT</p>
        <h1>
          Good systems start
          <br />
          <em>with a conversation.</em>
        </h1>
        <p>{profile.interests}</p>
        <a className="contact-email" href={`mailto:${profile.email}`}>
          {profile.email} <span aria-hidden="true">↗</span>
        </a>
      </div>
      <div className="contact-grid">
        <section>
          <p className="eyebrow">BASED IN</p>
          <h2>{profile.location}</h2>
          <p>{profile.timezone} · Open to remote roles</p>
        </section>
        <section>
          <p className="eyebrow">ELSEWHERE</p>
          <a
            className="text-link"
            href={profile.github}
            target="_blank"
            rel="noopener noreferrer"
          >
            GitHub ↗
          </a>
          <a
            className="text-link"
            href={profile.linkedin}
            target="_blank"
            rel="noopener noreferrer"
          >
            LinkedIn ↗
          </a>
        </section>
        <section>
          <p className="eyebrow">DIRECT CONTACT</p>
          <a className="text-link" href="tel:+250786889609">
            {profile.phone}
          </a>
          <Link className="text-link" to="/resume">
            View my CV →
          </Link>
        </section>
      </div>
    </>
  );
}
function NotFound() {
  return (
    <>
      <Meta title="Page not found" />
      <div className="page-intro">
        <p className="eyebrow">404 / PAGE NOT FOUND</p>
        <h1>A different path.</h1>
        <p>This page doesn’t exist. Explore my current work instead.</p>
        <Link className="button primary" to="/projects">
          <ActionLabel>Explore projects</ActionLabel>
        </Link>
      </div>
    </>
  );
}
function RouteReset() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
    const main = document.getElementById("main-content");
    if (main) main.focus({ preventScroll: true });
  }, [pathname]);
  return null;
}
export default function Portfolio({ theme, toggleTheme }) {
  return (
    <BrowserRouter>
      <a className="skip-link" href="#main-content">
        Skip to content
      </a>
      <Navigation theme={theme} toggleTheme={toggleTheme} />
      <RouteReset />
      <main id="main-content" className="site-main" tabIndex="-1">
        <Switch>
          <Route exact path="/" component={Home} />
          <Redirect exact from="/home" to="/" />
          <Redirect exact from="/splash" to="/" />
          <Route exact path="/projects" component={Projects} />
          <Route exact path="/projects/:slug" component={ProjectDetail} />
          <Route exact path="/about" component={About} />
          <Redirect exact from="/experience" to="/about" />
          <Route
            exact
            path="/education"
            render={() => <Education standalone />}
          />
          <Route exact path="/resume" component={Resume} />
          <Route exact path="/contact" component={Contact} />
          <Redirect exact from="/opensource" to="/projects" />
          <Route component={NotFound} />
        </Switch>
      </main>
      <footer className="site-footer">
        <Link className="brand-mark" to="/" aria-label="Home">
          PN<span>·</span>
        </Link>
        <p>
          © {new Date().getFullYear()} Patrick Niyigena
          <span>Software, AI, and the systems between.</span>
        </p>
        <div>
          <a href={profile.github} target="_blank" rel="noopener noreferrer">
            GitHub ↗
          </a>
          <a href={profile.linkedin} target="_blank" rel="noopener noreferrer">
            LinkedIn ↗
          </a>
          <a href={`mailto:${profile.email}`}>Email ↗</a>
        </div>
      </footer>
    </BrowserRouter>
  );
}
